import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import AddOrEditNoteModal from './AddOrEditNoteModal'
import { useFetchNotes } from 'hooks/use-notes'
import { TwinNoteSummary } from 'api/model/twinNoteSummary'
import { TwinNote } from 'api/model/twinNote'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { toastAtom } from 'state'
import { useSetRecoilState } from 'recoil'
import FtuxTooltip from 'components/FtuxTooltip'
import { useNavigate, useSearchParams } from 'react-router-dom'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import { OnboardingStep, SearchScopeType } from 'api/model'
import { useFtuxTooltip } from 'hooks/use-ftux'
import { BulkActionType } from './MyTwinFiles'
import BulkActionBar from 'components/BulkActionBar'
import List, { ListRenderer } from 'components/List'
import { formatTimestamp } from 'utils'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import BulkActionModal from 'components/BulkActionBar/BulkActionModal'
import SearchScopeSelector from './VisibilitySelector'
import { useActiveOrganization } from 'hooks/use-organization'
import { twinsDeleteNotes, twinsUpdateNotesScope } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import Selector from 'components/Selector'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { COLORS } from 'app-constants'

enum NoteFilter {
  'all' = 'All',
  'linked_note' = 'Web',
  'personal_note' = 'Personal',
}

const NoteFilterSelector = ({
  noteFilter,
  setNoteFilter,
}: {
  noteFilter: NoteFilter
  setNoteFilter: React.Dispatch<React.SetStateAction<NoteFilter>>
}) => {
  // get list of notes from NoteFilter enum
  const noteFilterOptions = useMemo(() => {
    return Object.values(NoteFilter)
  }, [])

  return (
    <Selector
      id="note-filter-selector"
      option={noteFilter}
      options={noteFilterOptions}
      setOption={setNoteFilter}
      isDarkBackdrop={false}
      width="240px"
    />
  )
}

const MyTwinNotes = ({ updateOnboardingStep }: { updateOnboardingStep: () => void }) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const navigate = useNavigate()
  const setToast = useSetRecoilState(toastAtom)

  const [noteId, setNoteId] = useState<string>('')
  const [isAddOrEditNoteModalOpen, setIsAddOrEditNoteModalOpen] = useState<boolean>(false)

  // used to trigger a notes refresh when a note is added or edited, this is to ensure that we show the indexing time which happens after a period of time
  const [shouldRefetchNotes, setShouldRefetchNotes] = useState<boolean>(false)

  const { notes, isLoading: isLoadingNotes, setNotes, refetchNotes } = useFetchNotes()

  const { isFtuxTooltipOpen, setIsFtuxTooltipOpen } = useFtuxTooltip()

  useEffect(() => {
    if (shouldRefetchNotes) {
      // refetch notes
      window.setTimeout(() => {
        refetchNotes()
        setShouldRefetchNotes(false)
      }, 5000)
    }
  }, [shouldRefetchNotes, refetchNotes])

  const onAddNoteClick = () => {
    setNoteId('')
    setIsAddOrEditNoteModalOpen(true)
  }

  // hydrate notes on client side
  const onAddNote = (note: TwinNote) => {
    const updatedNote: TwinNoteSummary = {
      id: note.id,
      title: note.title,
      twin_id: note.twin_id,
      summary: note.content.substring(0, 30), // we need this because the API doesn't return the summary
      date_updated: note.date_updated,
      date_created: note.date_created,
      url: note.url,
      search_scope: searchScope,
    }

    setNotes((prevNotes) => [updatedNote, ...prevNotes])
    setShouldRefetchNotes(true)
  }

  // hydrate notes on client side
  const onEditNote = (note: TwinNote) => {
    // loop through notes, find the existing note, and replace it
    const updatedNotes = notes.map((n) => {
      if (n.id === note.id) {
        return {
          id: note.id,
          title: note.title,
          twin_id: note.twin_id,
          summary: note.content.substring(0, 30), // we need this because the API doesn't return the summary
          date_updated: note.date_updated,
          date_created: note.date_created,
          url: note.url,
          search_scope: note.search_scope,
        }
      }

      return n
    })
    setNotes(updatedNotes)
    setShouldRefetchNotes(true)
  }

  // type of bulk action that is to be performed
  const [bulkActionType, setBulkActionType] = useState<BulkActionType | null>(null)
  const [isBulkActionModalOpen, setIsBulkActionModalOpen] = useState<boolean>(false)
  const onCloseBulkActionModal = () => {
    setIsBulkActionModalOpen(false)
  }
  const updateBulkActionType = (type: BulkActionType) => {
    setBulkActionType(type)
    setIsBulkActionModalOpen(true)
  }
  const onDismiss = () => {
    setCheckedNoteIds([])
  }

  // used in the bulk action modal for editing visibility
  const [searchScope, setSearchScope] = useState<SearchScopeType>(SearchScopeType.global)

  // used to keep track of which notes are checked
  const [checkedNoteIds, setCheckedNoteIds] = useState<string[]>([])
  const onItemChecked = (id: string) => {
    setCheckedNoteIds(() => {
      if (checkedNoteIds.includes(id)) {
        return checkedNoteIds.filter((i) => i !== id)
      }

      return [...checkedNoteIds, id]
    })
  }

  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<string>('')
  const [isDeleteNoteDialogOpen, setIsDeleteNoteDialogOpen] = useState<boolean>(false)

  const onUpdateScopeForNote = async (noteId: string, scope: SearchScopeType) => {
    if (!activeOrganization) {
      return
    }

    try {
      await twinsUpdateNotesScope(activeOrganization, {
        ids: [noteId],
        search_scope: scope,
      })
      setToast({
        severity: 'success',
        title: 'Visibility updated',
      })
      // update scope in recoil
      setNotes((prevNotes) => {
        return prevNotes.map((n) => {
          if (n.id === noteId) {
            return {
              ...n,
              search_scope: scope,
            }
          }

          return n
        })
      })
    } catch (e) {
      setToast({
        severity: 'error',
        title: 'Failed to edit visibility',
      })
    }
  }

  const getListRenderer = (
    items: TwinNoteSummary[],
    isCheckboxEnabled: boolean,
    isActionDisabled: boolean,
    hiddenColumnIndices?: number[]
  ) => {
    return {
      rows: items.map((tn) => {
        return {
          key: tn.id,
          columnWidth: (column) => {
            if (hiddenColumnIndices?.includes(column)) {
              return '0px'
            }
            switch (column) {
              case 0:
                return '24px'
              case 1:
                return '240px'
              case 2:
              case 3:
                return '120px'
              case 4:
                return '120px'
              case 5:
                return '120px'
              case 6:
              case 7:
                return '24px'
            }
          },
          columnEntry: (column) => {
            if (hiddenColumnIndices?.includes(column)) {
              return null
            }
            switch (column) {
              case 0:
                return (
                  <>
                    {isCheckboxEnabled && (
                      <Checkbox
                        disableRipple
                        color="primary"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                          onItemChecked(tn.id)
                        }}
                        checked={checkedNoteIds.includes(tn.id)}
                        sx={{
                          p: 0,
                          ml: '12px',
                        }}
                      />
                    )}
                  </>
                )
              case 1:
                return (
                  <Tooltip title={tn.title} enterDelay={1000}>
                    <Typography variant="pS">{tn.title}</Typography>
                  </Tooltip>
                )
              case 2:
                return <Typography variant="pS">{formatTimestamp(tn.date_updated)}</Typography>
              case 3:
                return (
                  <>
                    {tn.date_indexed ? (
                      <Typography variant="pS">{formatTimestamp(tn.date_indexed)}</Typography>
                    ) : (
                      <Typography variant="pS">In progress</Typography>
                    )}
                  </>
                )
              case 4:
                return (
                  <SearchScopeSelector
                    disabled={isActionDisabled}
                    searchScope={tn.search_scope}
                    setSearchScope={async (scope) => {
                      await onUpdateScopeForNote(tn.id, scope)
                    }}
                  />
                )
              case 5:
                return (
                  <Typography
                    variant="pS"
                    flex="0 0 120px"
                    mr={1}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {tn.url ? 'Web' : 'Personal'}
                  </Typography>
                )
              case 6:
                return (
                  !isActionDisabled && (
                    <IconButton
                      onClick={() => {
                        setNoteId(tn.id)
                      }}>
                      <EditOutlinedIcon
                        sx={{
                          height: '16px',
                          width: '16px',
                        }}
                      />
                    </IconButton>
                  )
                )
              case 7:
                return (
                  !isActionDisabled && (
                    <IconButton
                      onClick={() => {
                        setPendingDeleteNoteId(tn.id)
                        setIsDeleteNoteDialogOpen(true)
                      }}>
                      <DeleteOutlinedIcon
                        sx={{
                          height: '16px',
                          width: '16px',
                        }}
                      />
                    </IconButton>
                  )
                )
              default:
                return null
            }
          },
        }
      }),
      columnCount: 8,
      columnName: (column) => {
        if (hiddenColumnIndices?.includes(column)) {
          return null
        }
        switch (column) {
          case 0:
            return ''
          case 1:
            return (
              <Typography variant="h6" sx={{ width: '240px' }} component="span">
                Name
              </Typography>
            )
          case 2:
            return 'Last updated'
          case 3:
            return 'Last indexed'
          case 4:
            return (
              <Stack alignItems="center" direction="row" spacing="4px">
                <Typography variant="h6" component="span">
                  Visibility
                </Typography>

                <Tooltip
                  enterTouchDelay={0}
                  title={
                    'Documents marked as private will not be used to generate twin responses in interactions with other users.'
                  }>
                  <InfoOutlined
                    sx={{
                      height: '16px',
                      width: '16px',
                      color: COLORS.GRAY_3,
                    }}
                  />
                </Tooltip>
              </Stack>
            )
          case 5:
            return 'Tags'
          default:
            return null
        }
      },
    } as ListRenderer
  }

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const onBulkActionSubmit = async () => {
    if (!activeOrganization) {
      return
    }

    setIsSubmitting(true)

    switch (bulkActionType) {
      case BulkActionType.edit_visibility:
        try {
          await twinsUpdateNotesScope(activeOrganization, {
            ids: checkedNoteIds,
            search_scope: searchScope,
          })
          setToast({
            severity: 'success',
            title: 'Visibility updated',
          })
          // update scope in recoil
          setNotes((prevNotes) => {
            return prevNotes.map((n) => {
              if (checkedNoteIds.includes(n.id)) {
                return {
                  ...n,
                  search_scope: searchScope,
                }
              }

              return n
            })
          })
          setCheckedNoteIds([])
          setIsBulkActionModalOpen(false)
        } catch (e) {
          setToast({
            severity: 'error',
            title: 'Failed to edit visibility',
          })
        }

        break
      case BulkActionType.delete:
        try {
          await twinsDeleteNotes(activeOrganization, checkedNoteIds)
          refetchNotes()
          setToast({
            severity: 'success',
            title: 'Notes deleted',
          })
          setCheckedNoteIds([])
          setIsBulkActionModalOpen(false)
        } catch (e) {
          setToast({
            severity: 'error',
            title: 'Failed to delete notes',
          })
        }
        break
      default:
        break
    }

    setIsSubmitting(false)
  }

  const renderBulkActionModal = () => {
    switch (bulkActionType) {
      case BulkActionType.edit_visibility:
        return (
          <BulkActionModal
            isSubmitting={isSubmitting}
            isOpen={isBulkActionModalOpen}
            onSubmitClick={onBulkActionSubmit}
            submitButtonText="Done"
            headerText="Edit visibility"
            content={
              <Stack>
                <SearchScopeSelector
                  disabled={false}
                  entityName="Notes"
                  searchScope={searchScope}
                  setSearchScope={setSearchScope}
                />

                <Typography variant="pXS" mt={2} mb={4}>
                  Visibility settings will be applied to the following notes below.
                </Typography>

                <List
                  renderer={getListRenderer(
                    notes.filter((n) => checkedNoteIds.includes(n.id)),
                    false,
                    true,
                    [3] // we want to hide the Last Indexed column in the bulk action modal for the edit visibility action
                  )}
                  isLoading={isLoadingNotes}
                />
              </Stack>
            }
            onClose={onCloseBulkActionModal}
          />
        )
      case BulkActionType.delete:
        return (
          <BulkActionModal
            isSubmitting={isSubmitting}
            isOpen={isBulkActionModalOpen}
            onSubmitClick={onBulkActionSubmit}
            submitButtonText="Delete"
            onClose={onCloseBulkActionModal}
            headerText="Are you sure you want to delete the following notes?"
            content={
              <Stack>
                <List
                  renderer={getListRenderer(
                    notes.filter((n) => checkedNoteIds.includes(n.id)),
                    false,
                    true,
                    [3] // we want to hide the Last Indexed column in the bulk action modal for the delete action
                  )}
                  isLoading={isLoadingNotes}
                />
              </Stack>
            }
          />
        )
      default:
        return null
    }
  }

  // when the component is first mounted, we check to see if there is note id in the URL, if there is, we open the add/edit note modal
  const [searchParams] = useSearchParams()
  const noteIdFromUrl = searchParams.get('note_id')
  useEffect(() => {
    if (noteIdFromUrl && noteIdFromUrl !== noteId) {
      setNoteId(noteIdFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteIdFromUrl])

  useEffect(() => {
    if (noteId) {
      setIsAddOrEditNoteModalOpen(true)
    } else {
      setIsAddOrEditNoteModalOpen(false)
    }
  }, [noteId])

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const [noteFilter, setNoteFilter] = useState<NoteFilter>(NoteFilter.all)

  // filter notes based on search text and note filter
  const filteredNotes = useMemo(() => {
    return notes.filter((n) => {
      if (searchText) {
        return n.title.toLowerCase().includes(searchText.toLowerCase())
      }

      switch (noteFilter) {
        case NoteFilter.all:
          return true
        case NoteFilter.linked_note:
          return !!n.url
        case NoteFilter.personal_note:
          return !n.url
        default:
          return true
      }
    })
  }, [notes, searchText, noteFilter])

  const [isDeletingNote, setIsDeletingNote] = useState<boolean>(false)
  const onDeleteNoteConfirmed = async () => {
    if (!activeOrganization) {
      return
    }

    try {
      setIsDeletingNote(true)
      await twinsDeleteNotes(activeOrganization, [pendingDeleteNoteId])
      refetchNotes()
      setToast({
        severity: 'success',
        title: 'Note deleted',
      })
      setPendingDeleteNoteId('')
      setIsDeleteNoteDialogOpen(false)
    } catch (e) {
      setToast({
        severity: 'error',
        title: 'Failed to delete note',
      })
    } finally {
      setIsDeletingNote(false)
    }
  }

  // TODO: add loading state
  if (isLoadingNotes) {
    return null
  }

  return (
    <Stack
      borderLeft="1px solid #DDDDDD"
      width="100%"
      height="100vh"
      p={3}
      sx={{
        backgroundColor: COLORS.GRAY_8,
      }}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems="center"
        justifyContent="space-between"
        maxWidth="1024px">
        <Stack mb={2}>
          {!isMobile && (
            <Typography variant="h4" mb={2}>
              Notes
            </Typography>
          )}

          <Typography
            variant="pS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            Add snippets of text for your twin to reference when generating responses. You can also use URLs to fetch
            text from external websites.
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={2} maxWidth="1024px">
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <TextField
            hiddenLabel={true}
            placeholder="Search notes"
            value={searchText}
            onChange={onSearchTextChange}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      marginRight: '-10px',
                      color: COLORS.GRAY_3,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            style={{
              width: isMobile ? '100%' : '260px',
            }}
          />
          <FtuxTooltip
            open={isFtuxTooltipOpen}
            setOpen={setIsFtuxTooltipOpen}
            headerText="Add notes"
            subtitleText="Increase your twin's knowledge base by adding notes for your twin to reference when answering questions."
            progressText="2 of 6"
            onBackClick={() => {
              navigate('/my-twin')
            }}
            onNextClick={() => {
              navigate('/my-twin/emails')
            }}
            placement={isMobile ? 'bottom' : 'right-end'}
            onDismiss={() => {
              setIsFtuxTooltipOpen(false)
            }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={onAddNoteClick}
              startIcon={<AddOutlinedIcon />}
              sx={{
                height: '36px',
                width: '154px',
              }}>
              New note
            </Button>
          </FtuxTooltip>
        </Stack>

        <NoteFilterSelector noteFilter={noteFilter} setNoteFilter={setNoteFilter} />

        <BulkActionBar
          isOpen={checkedNoteIds.length > 0}
          buttons={[
            <Button
              size="medium"
              variant="outlined"
              onClick={() => {
                updateBulkActionType(BulkActionType.edit_visibility)
              }}
              sx={{
                color: COLORS.GRAY_0,
                background: COLORS.PURE_WHITE,
                border: `1px solid ${COLORS.GRAY_5}`,
                '&:hover': {
                  borderWidth: '1px',
                },
              }}>
              Edit visibility
            </Button>,

            <Button
              variant="outlined"
              size="medium"
              onClick={() => {
                updateBulkActionType(BulkActionType.delete)
              }}
              sx={{
                color: COLORS.GRAY_0,
                background: COLORS.PURE_WHITE,
                border: `1px solid ${COLORS.GRAY_5}`,
                '&:hover': {
                  borderWidth: '1px',
                },
              }}>
              Delete
            </Button>,
          ]}
          onDismiss={onDismiss}
        />
        {filteredNotes.length > 0 ? (
          <List renderer={getListRenderer(filteredNotes, true, false)} isLoading={isLoadingNotes} />
        ) : (
          <Typography
            variant="pS"
            sx={{
              color: COLORS.GRAY_3,
              textAlign: 'center',
              mt: 4,
            }}>
            No notes found
          </Typography>
        )}
        {renderBulkActionModal()}
      </Stack>

      {isAddOrEditNoteModalOpen && (
        <AddOrEditNoteModal
          noteId={noteId}
          onClose={() => {
            setIsAddOrEditNoteModalOpen(false)
            setNoteId('')
          }}
          onAddNote={onAddNote}
          onEditNote={onEditNote}
        />
      )}

      <Dialog open={isDeleteNoteDialogOpen}>
        <DialogTitle>Are you sure you want to delete this note?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingDeleteNoteId('')
              setIsDeleteNoteDialogOpen(false)
            }}
            sx={{
              color: COLORS.GRAY_0,
            }}
            disabled={isDeletingNote}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onDeleteNoteConfirmed} disabled={isDeletingNote}>
            Yes, delete it
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default withOnboardingStep(MyTwinNotes, OnboardingStep.my_twin_modal_shown)
